import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, Select, MenuItem, FormControl, InputLabel, TextField, CircularProgress } from '@material-ui/core';
import { httpClient } from '../utils/dataProvider';
import { PageHeader, Surface } from '../components/ui';
import { API_BASE_URL } from '../utils/config';

/**
 * P7.2 — the admin audit log viewer.
 *
 * The backend has written an immutable audit trail for some time, but no screen
 * read it: the data was invisible without database access. SPEC 24.6 requires
 * an immutable audit trail, and a trail nobody can read does not satisfy a
 * reviewer. P7.1's control depends on this being inspectable.
 *
 * Layout, PageHeader and Surface are reused from Logs.js per AC4 and SPEC 25.
 * No new design system.
 */

/** Page size. The endpoint paginates; the viewer must not pretend otherwise. */
export const PAGE_SIZE = 25;

/*
 * Rendered as "any action" rather than being omitted, so an unfiltered view is
 * an explicit choice the reader can see rather than an empty control.
 */
export const ANY = '__ANY__';

/**
 * The actions worth filtering by. Deliberately NOT hardcoded as the complete
 * set: the backend's ACTIONS list grows, and a stale allowlist here would
 * silently hide new entry types. The free-text box covers anything missing,
 * and unknown actions still appear in an unfiltered view.
 */
export const COMMON_ACTIONS = [
    'SUBSCRIPTION_GRANT',
    'SUBSCRIPTION_GRANT_REVOKE',
    'USER_UPDATE',
    'USER_STATUS_CHANGE',
    'PRICING_UPDATE',
];

/** 24-char hex. Matches the backend's own ObjectId validation. */
const OBJECT_ID = /^[0-9a-fA-F]{24}$/;

export const isObjectId = value => OBJECT_ID.test(String(value || '').trim());

/**
 * Builds the query string. Exported because the ordering and omission rules
 * below are the substance of this screen, and are asserted directly.
 */
export const buildQuery = ({ page = 1, action, targetId, admin, startDate, endDate } = {}) => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(PAGE_SIZE));

    if (action && action !== ANY) params.set('action', action);
    /*
     * A targetId that is not a valid ObjectId is NOT sent. The backend returns
     * an empty set for one, but sending junk would make the viewer depend on
     * that behaviour; the screen refuses locally and says so.
     */
    if (targetId && isObjectId(targetId)) params.set('targetId', targetId.trim());
    if (admin && isObjectId(admin)) params.set('admin', admin.trim());
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);

    return params.toString();
};

/** Renders the `changes` blob without pretending to understand its shape. */
export const describeChanges = changes => {
    if (changes === null || changes === undefined) return '—';
    if (typeof changes === 'string') return changes;
    try {
        const keys = Object.keys(changes);
        if (!keys.length) return '—';
        return keys.map(k => `${k}: ${JSON.stringify(changes[k])}`).join(', ');
    } catch (e) {
        return '—';
    }
};

/** An actor may have been deleted since acting; the entry must still read. */
export const describeActor = admin => {
    if (!admin) return 'Unknown admin';
    if (typeof admin === 'string') return admin;
    return admin.name || admin.email || admin._id || 'Unknown admin';
};

export const formatWhen = value => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-GB');
};

const styles = {
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e3e6ec', whiteSpace: 'nowrap' },
    td: { padding: '8px 10px', borderBottom: '1px solid #eef0f4', verticalAlign: 'top' },
    reason: { whiteSpace: 'pre-wrap', maxWidth: 320 },
    empty: { padding: 24, textAlign: 'center', color: '#6b7280' },
    pager: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 },
    btn: { padding: '7px 14px', borderRadius: 8, border: '1px solid #cfd5df', background: '#fff', cursor: 'pointer' },
    note: { fontSize: 12, color: '#6b7280', marginTop: 8 },
    warn: { fontSize: 12, color: '#8a6d1f', marginTop: 6 },
};

const AuditLogs = () => {
    const [rows, setRows] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalDocs, setTotalDocs] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [action, setAction] = useState(ANY);
    const [targetId, setTargetId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const targetIdRejected = targetId.trim().length > 0 && !isObjectId(targetId);

    const fetchPage = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const qs = buildQuery({ page, action, targetId, startDate, endDate });
            const { json } = await httpClient(`${API_BASE_URL}/adminAuditLogs?${qs}`, { method: 'GET' });
            setRows(json && Array.isArray(json.docs) ? json.docs : []);
            setTotalPages((json && json.totalPages) || 1);
            setTotalDocs((json && json.totalDocs) || 0);
        } catch (e) {
            /*
             * The rows are cleared on failure. Leaving the previous page
             * rendered under new filters would show entries that do not match
             * what the controls claim is being shown.
             */
            setRows([]);
            setTotalDocs(0);
            setError('Could not load the audit log. Nothing is being shown.');
        } finally {
            setLoading(false);
        }
    }, [page, action, targetId, startDate, endDate]);

    useEffect(() => { fetchPage(); }, [fetchPage]);

    /* Changing a filter must return to page 1, or the view silently empties. */
    const onFilter = setter => value => { setter(value); setPage(1); };

    const showing = useMemo(() => {
        if (!totalDocs) return 'No entries';
        const from = (page - 1) * PAGE_SIZE + 1;
        const to = Math.min(page * PAGE_SIZE, totalDocs);
        return `Showing ${from}–${to} of ${totalDocs}`;
    }, [page, totalDocs]);

    return (
        <>
            <PageHeader
                eyebrow="Governance"
                title="Admin Audit Log"
                description="Every administrative action, who took it, on whom, and why. Read-only and immutable."
            />

            <Surface title="Filters" className="sk-audit-filters">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                            <InputLabel id="audit-action-label">Action</InputLabel>
                            <Select
                                labelId="audit-action-label"
                                label="Action"
                                value={action}
                                onChange={e => onFilter(setAction)(e.target.value)}
                                inputProps={{ 'data-testid': 'audit-action' }}
                            >
                                <MenuItem value={ANY}>Any action</MenuItem>
                                {COMMON_ACTIONS.map(a => (
                                    <MenuItem key={a} value={a}>{a}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            label="Target user ID"
                            value={targetId}
                            onChange={e => onFilter(setTargetId)(e.target.value)}
                            inputProps={{ 'data-testid': 'audit-target' }}
                        />
                        {targetIdRejected && (
                            <div style={styles.warn} data-testid="audit-target-warning">
                                That is not a user ID, so it is being ignored — these results are NOT filtered by user.
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField
                            fullWidth variant="outlined" size="small" type="date" label="From"
                            InputLabelProps={{ shrink: true }}
                            value={startDate}
                            onChange={e => onFilter(setStartDate)(e.target.value)}
                            inputProps={{ 'data-testid': 'audit-from' }}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField
                            fullWidth variant="outlined" size="small" type="date" label="To"
                            InputLabelProps={{ shrink: true }}
                            value={endDate}
                            onChange={e => onFilter(setEndDate)(e.target.value)}
                            inputProps={{ 'data-testid': 'audit-to' }}
                        />
                    </Grid>
                </Grid>
            </Surface>

            <Surface title="Entries" description={showing} className="sk-audit-entries">
                {loading && <div style={styles.empty}><CircularProgress size={22} /></div>}

                {!loading && error && (
                    <div style={styles.empty} data-testid="audit-error">{error}</div>
                )}

                {!loading && !error && rows.length === 0 && (
                    <div style={styles.empty} data-testid="audit-empty">
                        No audit entries match these filters.
                    </div>
                )}

                {!loading && !error && rows.length > 0 && (
                    <table style={styles.table} data-testid="audit-table">
                        <thead>
                            <tr>
                                <th style={styles.th}>When</th>
                                <th style={styles.th}>Admin</th>
                                <th style={styles.th}>Action</th>
                                <th style={styles.th}>Target</th>
                                <th style={styles.th}>Reason</th>
                                <th style={styles.th}>Changes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr key={row._id} data-testid="audit-row">
                                    <td style={styles.td}>{formatWhen(row.createdAt)}</td>
                                    <td style={styles.td}>{describeActor(row.admin)}</td>
                                    <td style={styles.td}>{row.action}</td>
                                    <td style={styles.td}>
                                        {row.targetType ? `${row.targetType} ` : ''}{row.targetId || '—'}
                                    </td>
                                    <td style={{ ...styles.td, ...styles.reason }} data-testid="audit-reason">
                                        {row.reason || '—'}
                                    </td>
                                    <td style={styles.td}>{describeChanges(row.changes)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                <div style={styles.pager}>
                    <button
                        type="button" style={styles.btn} data-testid="audit-prev"
                        disabled={loading || page <= 1}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                        Previous
                    </button>
                    <span data-testid="audit-page">Page {page} of {totalPages}</span>
                    <button
                        type="button" style={styles.btn} data-testid="audit-next"
                        disabled={loading || page >= totalPages}
                        onClick={() => setPage(p => p + 1)}
                    >
                        Next
                    </button>
                </div>

                <div style={styles.note}>
                    Audit entries are immutable: they cannot be edited or deleted, here or anywhere else.
                </div>
            </Surface>
        </>
    );
};

export default AuditLogs;
